/**
 * useOrderPolling — polls /api/orders/{id} until terminal status reached.
 * Returns the latest order doc plus a manual `refetch` for child components.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";

const TERMINAL_STATUSES = ["sent_to_print", "shipped", "failed"];
const POLL_INTERVAL_MS = 3500;
const ERROR_RETRY_MS = 4000;

export function useOrderPolling(orderId) {
    const [order, setOrder] = useState(null);
    const [isPolling, setIsPolling] = useState(true);
    const cancelledRef = useRef(false);

    const refetch = useCallback(async () => {
        try {
            const { data } = await api.get(`/orders/${orderId}`);
            if (!cancelledRef.current) setOrder(data);
            return data;
        } catch {
            return null;
        }
    }, [orderId]);

    useEffect(() => {
        cancelledRef.current = false;
        let timeoutId = null;

        const tick = async () => {
            try {
                const { data } = await api.get(`/orders/${orderId}`);
                if (cancelledRef.current) return;
                setOrder(data);
                if (!TERMINAL_STATUSES.includes(data.status)) {
                    timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
                } else {
                    setIsPolling(false);
                }
            } catch {
                if (!cancelledRef.current) {
                    timeoutId = setTimeout(tick, ERROR_RETRY_MS);
                }
            }
        };
        tick();

        return () => {
            cancelledRef.current = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [orderId]);

    return { order, setOrder, isPolling, refetch };
}
